<script setup>
// SiteUp: popup de confirmação de agendamento WAHA — versão COMPONENTE VUE
// (substitui o antigo script injetado que interceptava XHR e era a fonte de
// vários bugs: 404/401, loop, timezone, data que não fixava). Aberto pelo
// KanbanBasicDataPanel logo após o operador agendar um compromisso, SÓ quando o
// toggle appointment_waha.enabled do Kanban está ligado. Mostra preview editável
// e envia o evento nativo do WhatsApp (POST kanban_appointment/send_confirmation).
import { ref, watch, computed } from 'vue';
import kanbanAPI from 'dashboard/api/kanban';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  itemId: { type: [String, Number], default: null },
  // ISO (UTC) do compromisso recém-agendado; pré-preenche o campo de data/hora.
  scheduledAt: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue', 'sent']);

const loading = ref(false);
const sending = ref(false);
const enabled = ref(true);
const message = ref('');
const previewError = ref('');
const form = ref({
  scheduled_at: '',
  duration_minutes: 60,
  location: '',
  responsible_name: '',
});

// ISO/UTC -> valor de <input datetime-local> no fuso do navegador (Brasília).
const toLocalInput = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const close = () => emit('update:modelValue', false);

const buildPayload = () => ({
  scheduled_at: form.value.scheduled_at,
  duration_minutes: form.value.duration_minutes,
  location: form.value.location,
  responsible_name: form.value.responsible_name,
});

const refreshPreview = async () => {
  if (!props.itemId) return;
  loading.value = true;
  previewError.value = '';
  try {
    const { data } = await kanbanAPI.previewAppointment(
      props.itemId,
      buildPayload()
    );
    enabled.value = data.enabled !== false;
    message.value = data.message || '';
    // Autopreenche local/responsável a partir da config do Kanban se vazios.
    if (!form.value.location && data.event?.location) {
      form.value.location = data.event.location;
    }
    if (!form.value.responsible_name && data.event?.responsible_name) {
      form.value.responsible_name = data.event.responsible_name;
    }
    if (data.event?.duration_minutes) {
      form.value.duration_minutes = data.event.duration_minutes;
    }
  } catch (e) {
    previewError.value =
      e?.response?.data?.error || e?.message || 'Erro ao gerar preview.';
  } finally {
    loading.value = false;
  }
};

const send = async () => {
  if (!props.itemId || sending.value) return;
  sending.value = true;
  try {
    await kanbanAPI.sendAppointmentConfirmation(props.itemId, buildPayload());
    emitter.emit('newToastMessage', {
      message: 'Agendamento enviado ao lead!',
      type: 'success',
    });
    emit('sent');
    close();
  } catch (e) {
    emitter.emit('newToastMessage', {
      message: `Erro ao enviar: ${e?.response?.data?.error || e?.message}`,
      type: 'error',
    });
  } finally {
    sending.value = false;
  }
};

// Ao abrir: reseta o form com a data recém-agendada e busca o preview.
watch(
  () => props.modelValue,
  open => {
    if (!open) return;
    form.value = {
      scheduled_at: toLocalInput(props.scheduledAt),
      duration_minutes: 60,
      location: '',
      responsible_name: '',
    };
    message.value = '';
    enabled.value = true;
    refreshPreview();
  }
);

const canSend = computed(
  () => enabled.value && !sending.value && !!form.value.scheduled_at
);
</script>

<template>
  <Teleport to="body">
    <div v-if="modelValue" class="su-appt-modal" @click.self="close">
      <div class="su-appt-card">
        <div class="su-appt-head">
          <div class="su-appt-title">Confirmar agendamento</div>
          <button class="su-appt-x" type="button" @click="close">×</button>
        </div>
        <div class="su-appt-body">
          <div v-if="!enabled" class="su-appt-warn">
            Configuração do Kanban está desmarcada. O preview funciona, mas o
            envio só libera depois de ativar o agendamento nas configurações.
          </div>
          <div class="su-appt-grid">
            <div class="su-appt-field">
              <label>Data e hora</label>
              <input
                v-model="form.scheduled_at"
                type="datetime-local"
              />
            </div>
            <div class="su-appt-field">
              <label>Duração (min)</label>
              <input v-model.number="form.duration_minutes" type="number" min="5" />
            </div>
          </div>
          <div class="su-appt-field">
            <label>Local/endereço</label>
            <input
              v-model="form.location"
              placeholder="Auto preenche com o Kanban se vazio"
            />
          </div>
          <div class="su-appt-field">
            <label>Responsável</label>
            <input v-model="form.responsible_name" />
          </div>
          <div class="su-appt-field">
            <label>Preview para o lead</label>
            <div class="su-appt-preview">
              <span v-if="loading">Gerando preview...</span>
              <span v-else-if="previewError" class="su-appt-err">{{ previewError }}</span>
              <span v-else>{{ message || '(sem mensagem)' }}</span>
            </div>
          </div>
        </div>
        <div class="su-appt-actions">
          <button
            class="su-appt-btn su-appt-ghost"
            type="button"
            :disabled="loading"
            @click="refreshPreview"
          >
            Atualizar preview
          </button>
          <button
            class="su-appt-btn"
            type="button"
            :disabled="!canSend"
            @click="send"
          >
            {{ sending ? 'Enviando...' : 'Confirmar e enviar' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.su-appt-modal {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
}
.su-appt-card {
  width: min(640px, 96vw);
  max-height: 90vh;
  overflow: auto;
  background: #fff;
  color: #0f172a;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.35);
}
:global(.dark) .su-appt-card {
  background: #0f172a;
  color: #e2e8f0;
  border-color: rgba(148, 163, 184, 0.22);
}
.su-appt-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.22);
}
.su-appt-title {
  font-size: 16px;
  font-weight: 700;
}
.su-appt-body {
  padding: 18px 20px;
  display: grid;
  gap: 14px;
}
.su-appt-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.su-appt-field {
  display: grid;
  gap: 5px;
}
.su-appt-field label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}
.su-appt-field input {
  width: 100%;
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 10px;
  background: transparent;
  color: inherit;
  padding: 9px 10px;
  font-size: 13px;
}
.su-appt-preview {
  border: 1px solid rgba(37, 99, 235, 0.25);
  background: rgba(37, 99, 235, 0.06);
  border-radius: 12px;
  padding: 12px;
  white-space: pre-wrap;
  font-size: 13px;
  line-height: 1.45;
}
.su-appt-err {
  color: #dc2626;
}
.su-appt-warn {
  border: 1px solid rgba(245, 158, 11, 0.35);
  background: rgba(245, 158, 11, 0.1);
  border-radius: 10px;
  padding: 10px;
  font-size: 12px;
  color: #92400e;
}
:global(.dark) .su-appt-warn {
  color: #fbbf24;
}
.su-appt-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid rgba(148, 163, 184, 0.22);
}
.su-appt-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(37, 99, 235, 0.35);
  background: #2563eb;
  color: #fff;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.su-appt-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.su-appt-ghost {
  background: transparent;
  color: #2563eb;
}
.su-appt-x {
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 22px;
  cursor: pointer;
  line-height: 1;
}
</style>
